import { resetTenantColors } from '@/utils/tenantTheme';
import { getHostTenant, tenantLoginPath } from '@/services/tenantHost';

export default {
  namespaced: true,
  state: () => ({
    user: {
      id: null,
      nome: null,
      email: null,
      role: null,
      usuario_login_alternativo: null,
      status: null,
      created_at: null,
      updated_at: null,
    },
    accessToken: null,
    refreshToken: null,
    user_permissions: [],
    isLoading: false,
    userAuth: {},
  }),
  mutations: {
    SET_USER(state, payload) {
      localStorage.setItem('userData', JSON.stringify(payload));
      state.user = payload.userData || payload;
      state.accessToken = payload.accessToken;
      state.refreshToken = payload.refreshToken;
    },
    SET_USER_PERMISSIONS(state, payload) {
      localStorage.setItem('userPermissions', JSON.stringify(payload));
      state.user_permissions = payload;
    },
    SET_ACCESS_TOKEN(state, payload) {
      localStorage.setItem('accessToken', payload);
      state.accessToken = payload;
    },
    SET_REFRESH_TOKEN(state, payload) {
      localStorage.setItem('refreshToken', payload);
      state.refreshToken = payload;
    },
    SET_IS_LOADING(state, payload) {
      state.isLoading = payload;
    },
    SET_USER_AUTH(state, payload) {
      state.userAuth = payload;
    },
  },
  getters: {
    perfilAcessoNome() {
      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
        if (perfilLogado && perfilLogado.perfil_acesso_nome) {
          return perfilLogado.perfil_acesso_nome;
        }
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (userData && Array.isArray(userData.perfils) && userData.perfils[0]) {
          return userData.perfils[0].perfil_acesso_nome || '';
        }
      } catch (_) {}
      return '';
    },
    GET_USER(state) {
      const userLocal = JSON.parse(localStorage.getItem('userData')) || state.user;
      return {
        ...userLocal,
        permissions: state.user_permissions,
      };
    },
    GET_USER_PERMISSIONS(state) {
      return JSON.parse(localStorage.getItem('userPermissions')) || state.user_permissions;
    },
    IS_LOGGED_IN(state) {
      const userLocal = JSON.parse(localStorage.getItem('userData')) || state.user;
      return userLocal.id !== null;
    },
    GET_ACCESS_TOKEN(state) {
      return localStorage.getItem('accessToken') || state.accessToken;
    },
    GET_REFRESH_TOKEN(state) {
      return localStorage.getItem('refreshToken') || state.refreshToken;
    },
    GET_IS_LOADING(state) {
      return state.isLoading;
    },
    GET_USER_AUTH(state) {
      return state.userAuth;
    },
  },
  actions: {
    LOGOUT_USER({ commit, dispatch }, options = {}) {
      const tenantSlug = localStorage.getItem('tenantSlug') || '';
      const preferenceKeys = ['vuexy-skin'];
      if (localStorage.getItem('rememberMe') === 'true') {
        preferenceKeys.push('rememberMe', 'email', 'password');
      }
      const preferences = preferenceKeys
        .map(key => [key, localStorage.getItem(key)])
        .filter(([, value]) => value !== null);
      // ── 1. Limpa imediatamente o estado local do widget.
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('salesflow:logout'));
        }
      } catch (_) { /* noop */ }

      // ── 2. Best-effort: encerra a sessão do chat no backend sem passar pelo
      // interceptor axios. Com token expirado, reutilizar o axios dispararia
      // LOGOUT_USER recursivamente a cada 401.
      try {
        const token = localStorage.getItem('accessToken');
        if (token && typeof window !== 'undefined' && typeof window.fetch === 'function') {
          let baseURL = process.env.VUE_APP_HOST_API_BACKEND_PROD;
          if (process.env.VUE_APP_MODE_APP !== 'production' && process.env.VUE_APP_HOST_API_BACKEND_DEV) {
            baseURL = process.env.VUE_APP_HOST_API_BACKEND_DEV;
          }
          baseURL = getHostTenant() ? '/api' : (baseURL || `${window.location.origin}/api`);
          const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          };
          const impersonateSlug = localStorage.getItem('dkpImpersonateSlug');
          if (impersonateSlug && impersonateSlug !== 'dkp') {
            headers['x-tenant-slug'] = 'dkp';
            headers['x-admin-view-tenant'] = impersonateSlug;
          } else {
            headers['x-tenant-slug'] = localStorage.getItem('tenantSlug') || process.env.VUE_APP_TENANT_SLUG || 'dkp';
          }
          const perfil = JSON.parse(localStorage.getItem('perfilLogado') || '{}');
          if (perfil.id_perfil_acesso != null) headers['x-perfil-id'] = String(perfil.id_perfil_acesso);
          window.fetch(`${String(baseURL).replace(/\/$/, '')}/ai-chat/reset`, {
            method: 'POST',
            headers,
            body: '{}',
            keepalive: true,
          }).catch(() => {});
        }
      } catch (_) { /* noop */ }

      resetTenantColors();
      commit('SET_USER', {
        id: null,
        nome: null,
        email: null,
        role: null,
        usuario_login_alternativo: null,
        status: null,
        created_at: null,
        updated_at: null,
      });
      commit('SET_USER_PERMISSIONS', []);
      commit('SET_ACCESS_TOKEN', null);
      commit('SET_REFRESH_TOKEN', null);
      commit('SET_USER_AUTH', {});
      commit('SET_IS_LOADING', false);
      localStorage.removeItem('userData');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tenant_cor_primaria');
      localStorage.removeItem('tenant_cor_secundaria');
      localStorage.removeItem('tenant_logo_url');
      localStorage.removeItem('tenant_cached_slug');
      localStorage.removeItem('dkpImpersonateSlug');
      // Remover estado persistido do PTC_Pedidos
      localStorage.removeItem('estado');

      localStorage.removeItem('activeDraftId');
      localStorage.removeItem('pedido_drafts_index');
      Object.keys(localStorage)
        .filter((key) => key.startsWith('pedido_draft_') || key.startsWith('infoPedidoForm_') || key.startsWith('pedido_cart_'))
        .forEach((key) => localStorage.removeItem(key));

      dispatch('ECommercerStore/clearCart', null, { root: true });
      dispatch('PedidosStore/clearPedidoAndItemPedido', null, { root: true });
      dispatch('intensPedido/clearPedidoData', null, { root: true });

      // Uma nova identidade não pode herdar filtros, perfis ou caches da sessão
      // anterior. Preserva apenas tema, login lembrado por opção explícita
      // e a empresa necessária para voltar ao login correto (não a sessão).
      localStorage.clear();
      sessionStorage.clear();
      if (tenantSlug) localStorage.setItem('tenantSlug', tenantSlug);
      preferences.forEach(([key, value]) => localStorage.setItem(key, value));

      // router.push mantém Vuex, getters cacheados, componentes e requisições
      // da sessão anterior vivos. Uma navegação completa descarta tudo isso.
      if (!options.skipRedirect) window.location.replace(tenantLoginPath(tenantSlug));
    },
  },
};
